import bind from 'bind-decorator';
import { AnalyticsApi } from 'global/api';
import { ROUTES } from 'global/constants';
import {
    AnalyticsEventEventTypeEnum,
    ContentSource,
    OrganizationSummaryOrganizationTypeEnum,
} from 'labxchange-client';
import messages from 'library/displayMessages';
import * as React from 'react';
import * as classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { WrappedMessage } from 'utils';
import { Icon } from 'elements/components/Icons';

interface Props extends ContentSource {
    itemId: string;
    className?: string;
}

/**
 * The box at the top right of a unit page that displays where content comes from.
 */
export class ContentSourceInfoBox extends React.PureComponent<Props, {}> {

    private pathParams = window.location.search;
    private pathname = window.location.pathname;

    /// Verifies if 'infoUrl' and 'orgUrl' has the same hostname
    public showProfile(infoUrlString: string, orgUrlString: string) {
      try {
        const infoUrl = new URL(infoUrlString);
        const orgUrl = new URL(orgUrlString);
        return infoUrl.hostname !== orgUrl.hostname;
      } catch (_) {
        return true;
      }
    }

    public render() {
        let orgUrl = '#';
        let orgIsPartner = false;
        let orgUrlIsExternal = false;
        if (this.props.provider.organizationType === OrganizationSummaryOrganizationTypeEnum.SPONSOR ||
            this.props.provider.organizationType === OrganizationSummaryOrganizationTypeEnum.PARTNER
        ) {
            orgUrl = ROUTES.Organizations.PROFILE_SLUG(this.props.provider.slug);
            orgIsPartner = true;
        } else if (this.props.provider.url) {
            orgUrl = this.props.provider.url;
            orgUrlIsExternal = true;
        }
        const infoUrl = this.props.infoUrl;
        const showProfileButton = this.showProfile(infoUrl, orgUrl);
        const providerLink = (content: any) => {
            if (orgIsPartner) {
                return (
                    <NavLink to={orgUrl} className={'item-source-provider-url'}>
                        {content}
                    </NavLink>
                );
            } else {
                return (
                    <a
                        href={orgUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={'item-source-provider-url'}
                    >
                        {content}
                    </a>
                );
            }
        };

        return (
            /* The <div> element has a child elements that allow keyboard interaction */
            /* eslint-disable jsx-a11y/click-events-have-key-events */
            /* eslint-disable jsx-a11y/no-static-element-interactions */
            <div className={`source ${this.props.className || ''}`} onClick={this.onLinkClick}>
                <div>
                    {this.props.provider.largeLogoUrl ?
                        providerLink(<img
                            className='mb-3' src={this.props.provider.largeLogoUrl} alt={this.props.provider.name}
                        />)
                    : null}
                    <p className='source-text'>
                        {
                            this.props.title ?
                                <WrappedMessage
                                    message={messages.contentSourceVerbose}
                                    values={{
                                        providerName: providerLink(this.props.provider.name),
                                        sourceName:
                                            <a
                                                href={infoUrl}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className={'item-source-source-url'}
                                            >
                                                {this.props.title}
                                            </a>,
                                    }}
                                />
                                :
                                <WrappedMessage
                                    message={messages.contentSourceSimple}
                                    values={{providerName: providerLink(this.props.provider.name)}}
                                />
                        }
                    </p>
                    {infoUrl ? this.props.showEnrollAction ? <a
                        href={infoUrl}
                        className='source-action primary-action nudging-button-primary mt-2 item-source-enroll-url'
                        target='_blank' rel='noopener noreferrer'
                    >
                        <Icon name={'link-external'} zoom={'x0.8'} className={'external-link-icon'}/>
                        <WrappedMessage message={messages.contentSourceEnrollAction}/>
                    </a>: <a
                        href={infoUrl}
                        className='source-action secondary-action nudging-button-secondary mt-2 item-source-info-url'
                        target='_blank' rel='noopener noreferrer'
                    >
                        <Icon name={'link-external'} zoom={'x0.8'} className={'external-link-icon'}/>
                        {this.props.infoButtonText || <WrappedMessage message={messages.contentSourceInfoButtonText}/>}
                    </a> : null}
                    {showProfileButton ? <a
                        href={orgUrl}
                        className={classNames(
                            'source-action',
                            'nudging-button-secondary',
                            'mt-2',
                            'item-source-profile-url',
                            {'blue': infoUrl}
                        )}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {orgUrlIsExternal && <Icon name={'link-external'} zoom={'x0.8'} className={'external-link-icon'}/>}
                        <WrappedMessage message={messages.viewOwnerProfileButton} />
                    </a> : null}
                </div>
            </div>
        );
    }

    @bind private onLinkClick(event: React.MouseEvent) {
        const eventTargetClassNames = (event.target as HTMLElement).className.split(' ');
        let clickEventType;
        let orgEventType;
        if (eventTargetClassNames.includes('item-source-provider-url')) {
            clickEventType = AnalyticsEventEventTypeEnum.AssetSourceProviderLinkClicked;
            orgEventType = AnalyticsEventEventTypeEnum.OrganizationLabXchangeProfileVisited;
        } else if (eventTargetClassNames.includes('item-source-source-url')) {
            clickEventType = AnalyticsEventEventTypeEnum.AssetSourceSourceLinkClicked;
            orgEventType = AnalyticsEventEventTypeEnum.OrganizationWebsiteClicked;
        } else if (eventTargetClassNames.includes('item-source-info-url')) {
            clickEventType = AnalyticsEventEventTypeEnum.AssetSourceInfoLinkClicked;
            orgEventType = AnalyticsEventEventTypeEnum.OrganizationWebsiteClicked;
        } else if (eventTargetClassNames.includes('item-source-enroll-url')) {
            clickEventType = AnalyticsEventEventTypeEnum.AssetSourceEnrollLinkClicked;
        } else if (eventTargetClassNames.includes('item-source-profile-url')) {
            orgEventType = AnalyticsEventEventTypeEnum.OrganizationLabXchangeProfileVisited;
        }

        if (clickEventType) {
            AnalyticsApi.create({
                data: {
                    eventType: clickEventType,
                    objectId: this.props.id,
                    data: {
                        itemId: this.props.itemId,
                    },
                },
            });
        }
        if (orgEventType) {
            /// From a normal asset
            let fromValue = 'asset';
            if (this.pathParams.search('lx-book') >= 0) { /// From a textbook
                fromValue = 'textbook';
            }
            else if (this.pathname.search('lx-pathway') >= 0) { /// From a pathway
                fromValue = 'pathway';
            }

            AnalyticsApi.create({
                data: {
                    eventType: orgEventType,
                    orgSlug: this.props.provider.slug,
                    objectId: this.props.id,
                    data: {
                        from: fromValue,
                        pathname: this.pathname,
                    },
                },
            });
        }
    }

}
